import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Clock, AlertTriangle, XCircle, CheckCircle, Calendar, 
  Plus, RefreshCw, Mail, Building, Users, DollarSign, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialManagement() {
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [summary, setSummary] = useState(null);
  const [expiringClinics, setExpiringClinics] = useState([]);
  const [expiredClinics, setExpiredClinics] = useState([]);
  const [blockedClinics, setBlockedClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [extendReason, setExtendReason] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, expiringRes, expiredRes, blockedRes] = await Promise.all([
        axios.get(`${API_URL}/api/trial-management/dashboard/summary`, { headers }),
        axios.get(`${API_URL}/api/trial-management/clinics/expiring?days=14`, { headers }),
        axios.get(`${API_URL}/api/trial-management/clinics/expired?include_grace=true`, { headers }),
        axios.get(`${API_URL}/api/trial-management/clinics/blocked`, { headers })
      ]);

      setSummary(summaryRes.data);
      setExpiringClinics(expiringRes.data);
      setExpiredClinics(expiredRes.data);
      setBlockedClinics(blockedRes.data);
    } catch (error) {
      console.error('Failed to fetch trial data:', error);
      toast.error('Failed to load trial management data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExtendTrial = async () => {
    if (!selectedClinic) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/trial-management/clinics/${selectedClinic.clinic_id}/extend-trial`,
        { days: extendDays, reason: extendReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Trial extended by ${extendDays} days for ${selectedClinic.clinic_name}`);
      setShowExtendDialog(false);
      setSelectedClinic(null);
      setExtendDays(7);
      setExtendReason('');
      fetchData();
    } catch (error) {
      console.error('Failed to extend trial:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    }
  };

  const handleSendReminders = async () => {
    try {
      setSendingReminders(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trial-management/run-reminder-check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Reminder check complete! ${response.data.reminders_sent_today} emails sent today.`);
      fetchData();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast.error(error.response?.data?.detail || 'Failed to run reminder check');
    } finally {
      setSendingReminders(false);
    }
  };

  const openExtendDialog = (clinic) => {
    setSelectedClinic(clinic);
    setShowExtendDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trial-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Trial Management</h1>
          <p className="text-gray-600">Monitor and manage clinic trial periods</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleSendReminders} 
            variant="outline"
            disabled={sendingReminders}
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
          >
            {sendingReminders ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send Reminders Now
          </Button>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Trials</p>
                  <p className="text-2xl font-bold text-teal-600">{summary.active_trials}</p>
                </div>
                <Clock className="h-8 w-8 text-teal-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Expiring Soon</p>
                  <p className="text-2xl font-bold text-amber-600">{summary.expiring_within_7_days}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-amber-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Grace Period</p>
                  <p className="text-2xl font-bold text-orange-600">{summary.in_grace_period}</p>
                </div>
                <Calendar className="h-8 w-8 text-orange-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Blocked</p>
                  <p className="text-2xl font-bold text-red-600">{summary.blocked}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Paid Subscriptions</p>
                  <p className="text-2xl font-bold text-green-600">{summary.paid_subscriptions}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <p className="text-blue-800">
                <strong>Grace Period:</strong> {summary?.grace_period_days || 7} days after trial expires. 
                Clinics are blocked after the grace period ends.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <p className="text-blue-800">
                <strong>Automated Reminders:</strong> Emails sent at 14, 7, 3, and 1 day before trial expiration (daily at 9 AM UTC).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="expiring" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="expiring" className="relative">
            Expiring Soon
            {expiringClinics.length > 0 && (
              <Badge className="ml-2 bg-amber-500">{expiringClinics.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="grace" className="relative">
            In Grace Period
            {expiredClinics.filter(c => c.is_in_grace_period).length > 0 && (
              <Badge className="ml-2 bg-orange-500">
                {expiredClinics.filter(c => c.is_in_grace_period).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="blocked" className="relative">
            Blocked
            {blockedClinics.length > 0 && (
              <Badge className="ml-2 bg-red-500">{blockedClinics.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Expiring Soon Tab */}
        <TabsContent value="expiring">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Clinics with Trials Expiring in 14 Days
              </CardTitle>
              <CardDescription>
                These clinics should be contacted about subscribing
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expiringClinics.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No trials expiring soon</p>
              ) : (
                <div className="space-y-3">
                  {expiringClinics.map((clinic) => (
                    <ClinicRow 
                      key={clinic.clinic_id} 
                      clinic={clinic}
                      onExtend={() => openExtendDialog(clinic)}
                      statusType="expiring"
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Grace Period Tab */}
        <TabsContent value="grace">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-600" />
                Clinics in Grace Period
              </CardTitle>
              <CardDescription>
                Trials expired but still within grace period - urgent action needed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expiredClinics.filter(c => c.is_in_grace_period).length === 0 ? (
                <p className="text-center text-gray-500 py-8">No clinics in grace period</p>
              ) : (
                <div className="space-y-3">
                  {expiredClinics.filter(c => c.is_in_grace_period).map((clinic) => (
                    <ClinicRow 
                      key={clinic.clinic_id} 
                      clinic={clinic}
                      onExtend={() => openExtendDialog(clinic)}
                      statusType="grace"
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blocked Tab */}
        <TabsContent value="blocked">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Blocked Clinics
              </CardTitle>
              <CardDescription>
                These clinics have no access - trial and grace period expired
              </CardDescription>
            </CardHeader>
            <CardContent>
              {blockedClinics.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No blocked clinics</p>
              ) : (
                <div className="space-y-3">
                  {blockedClinics.map((clinic) => (
                    <ClinicRow 
                      key={clinic.clinic_id} 
                      clinic={clinic}
                      onExtend={() => openExtendDialog(clinic)}
                      statusType="blocked"
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Extend Trial Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Trial Period</DialogTitle>
            <DialogDescription>
              Extend the trial for {selectedClinic?.clinic_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Days to Extend</label>
              <Input
                type="number"
                min={1}
                max={90}
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum 90 days</p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                value={extendReason}
                onChange={(e) => setExtendReason(e.target.value)}
                placeholder="e.g., Customer requested more time to evaluate, Technical issues during trial..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowExtendDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleExtendTrial}>
              <Plus className="h-4 w-4 mr-2" />
              Extend by {extendDays} Days
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ClinicRow({ clinic, onExtend, statusType }) {
  const getBadge = () => {
    switch (statusType) {
      case 'expiring':
        return (
          <Badge className={`${
            clinic.days_remaining <= 3 ? 'bg-red-500' : 
            clinic.days_remaining <= 7 ? 'bg-amber-500' : 'bg-yellow-500'
          }`}>
            {clinic.days_remaining} day{clinic.days_remaining !== 1 ? 's' : ''} left
          </Badge>
        );
      case 'grace':
        return (
          <Badge className="bg-orange-500">
            {clinic.grace_remaining} day{clinic.grace_remaining !== 1 ? 's' : ''} grace left
          </Badge>
        );
      case 'blocked':
        return (
          <Badge className="bg-red-600">
            Blocked {clinic.days_since_expiry} days ago
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
          <Building className="h-5 w-5 text-teal-600" />
        </div>
        <div>
          <p className="font-medium">{clinic.clinic_name}</p>
          <p className="text-sm text-gray-500">{clinic.contact_email}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        {getBadge()}
        <Button 
          size="sm" 
          variant="outline"
          onClick={onExtend}
          data-testid={`extend-trial-${clinic.clinic_id}`}
        >
          <Plus className="h-4 w-4 mr-1" />
          Extend
        </Button>
      </div>
    </div>
  );
}
